// i18n/useT.js — the in-house translation hook every NEW surface should import,
// instead of importing react-i18next directly.
//
// WHY a wrapper: the framework choice (react-i18next vs the cheap
// Google-Translate widget) is still an ASSUMPTION pending dev/Gordon sign-off
// (see i18n/README.md + the PR body). Routing every surface through `useT`
// means that if the decision changes, ONLY this file changes — not the dozens
// of surfaces that consumed it. This is the cheap insurance for a best-guess.
//
// Usage:
//   import { useT } from '@/i18n/useT';            // or relative path
//   const { t } = useT('storage');                 // pick the namespace
//   <h2>{t('lifecycle.heading')}</h2>
//   <p>{t('notice.warn.body', { remaining, limit })}</p>  // interpolation

import { useTranslation } from 'react-i18next';
// Self-bootstrapping: react-i18next's useTranslation needs an initialized
// i18next singleton to resolve keys against. In the real app that init runs
// via <I18nProvider> in App.js before anything renders, but most component
// tests mount a surface directly (e.g. `render(<Login />)`) without going
// through App.js/I18nProvider at all. Importing the bootstrap here — a
// module that only runs its side-effecting `i18next.init()` once, cached by
// the module system — means any surface that adopts `useT` resolves real
// keys in isolation, in every test, with zero test-file changes required.
import './index';

/**
 * @param {string|string[]} [ns='common'] - namespace(s) to load.
 * @returns {{ t: Function, i18n: import('i18next').i18n, ready: boolean }}
 */
export function useT(ns = 'common') {
  const { t, i18n, ready } = useTranslation(ns);
  return { t, i18n, ready };
}

export default useT;
